import { Box, Button, Container, Grid, LinearProgress, Typography, makeStyles } from '@material-ui/core';
import { useState } from 'react';
import { Loading } from '../../../icons/Loading';
import { useAppStateContext } from '../../AppStateProvider/AppStateProvider';
import { ConnectionFailed } from './ConnectionFailed/ConnectionFailed';
import { ConnectionModal } from './ConnectionModal/ConnectionModal';
import { ConnectionSuccess } from './ConnectionSuccess/ConnectionSuccess';

// Per-test safety timeouts are now managed within each hook:
// - usePreflightTest: 30s
// - useSTUNTest: 90s
// - useICETest: 45s
// - useBitrateTest: uses its own testDuration + timeout

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  textBox: {
    width: '340px',
    height: '48px',
    textAlign: 'center',
  },
  loading: {
    animation: '$loading 1.5s linear infinite',
    display: 'flex',
  },
  progressContainer: {
    width: '300px',
    marginTop: '20px',
  },
  progressText: {
    marginTop: '10px',
    fontSize: '0.9rem',
    color: '#666',
  },
  cancelButton: {
    marginTop: '20px',
    backgroundColor: '#f44336',
    color: 'white',
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
  },
  '@keyframes loading': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
});

export function Connectivity() {
  const { state, cancelConnectivityTests } = useAppStateContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const classes = useStyles();

  const handleCancelTests = () => {
    cancelConnectivityTests();
  };

  // Helper function to format seconds into MM:SS format
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get selected test duration in seconds
  const testDurationMs = state.selectedCallSimulationDuration || 600000; // fallback to 10 minutes
  const testDurationSeconds = testDurationMs / 1000;

  // Helper function to format duration for display
  const formatDuration = (milliseconds: number) => {
    const seconds = milliseconds / 1000;
    if (seconds < 60) {
      return `${seconds} seconds`;
    } else {
      const minutes = Math.floor(seconds / 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  };

  const signalingGateway = state.preflightTest.signalingGatewayReachable ? 'Reachable' : 'Unreachable';
  const turnServers = state.preflightTest.turnServersReachable ? 'Reachable' : 'Unreachable';

  const connectionFailed =
    state.preflightTestFinished &&
    state.stunTest.finished &&
    state.iceTest.finished &&
    (state.preflightTest.error !== null ||
      !state.preflightTest.signalingGatewayReachable ||
      !state.preflightTest.turnServersReachable ||
      state.stunTest.error !== null ||
      state.iceTest.error !== null);

  return (
    <>
      <ConnectionModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        serviceStatuses={state.twilioStatus}
        signalingGateway={signalingGateway}
        turnServers={turnServers}
      />
      {
        /* If preflight test, bitrate test, STUN test, or ICE test haven't completed, display loading screen otherwise, display connectivity results: */
        state.preflightTestInProgress || state.bitrateTestInProgress || state.stunTest.inProgress || state.iceTest.inProgress ? (
          <Container>
            <Grid container className={classes.container}>
              <Typography variant="h1" gutterBottom>
                Running Extended Connectivity Tests
              </Typography>
              <Typography variant="body1" gutterBottom className={classes.textBox}>
                {state.bitrateTestInProgress
                  ? `Performing comprehensive network quality analysis (${formatDuration(testDurationMs)})...`
                  : state.stunTest.inProgress
                  ? (state.stunTest.progress || 'Testing firewall and NAT configuration...')
                  : state.iceTest.inProgress
                  ? (state.iceTest.progress || 'Testing connection path selection...')
                  : 'Testing network connectivity and quality...'}
              </Typography>
              <div className={classes.loading}>
                <Loading />
              </div>

              {/* Progress tracking for extended tests */}
              {state.bitrateTestInProgress && state.bitrateTestProgress && (
                <Box className={classes.progressContainer}>
                  <LinearProgress
                    variant="determinate"
                    value={state.bitrateTestProgress}
                    style={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography className={classes.progressText} align="center">
                    Progress: {Math.round(state.bitrateTestProgress)}% (
                    {formatTime((testDurationSeconds * state.bitrateTestProgress) / 100)} / {formatTime(testDurationSeconds)})
                  </Typography>
                  {state.currentBitrate && (
                    <Typography className={classes.progressText} align="center">
                      Current bitrate: {Math.round(state.currentBitrate)} kbps
                    </Typography>
                  )}
                  {state.qualityMetrics.overallAverage && (
                    <Typography className={classes.progressText} align="center">
                      Average: {Math.round(state.qualityMetrics.overallAverage)} kbps | Stability:{' '}
                      {Math.round(state.qualityMetrics.stabilityScore || 0)}%
                    </Typography>
                  )}
                </Box>
              )}

              {/* Session expired warning */}
              {state.appIsExpired && (
                <Typography style={{ color: '#f44336', marginTop: '16px', fontWeight: 500 }} align="center">
                  Session expired — please reload to run a new test.
                </Typography>
              )}

              {/* Cancel button */}
              <Button variant="outlined" className={classes.cancelButton} onClick={handleCancelTests}>
                Cancel the test
              </Button>
            </Grid>
          </Container>
        ) : connectionFailed ? (
          <ConnectionFailed />
        ) : (
          <ConnectionSuccess />
        )
      }
    </>
  );
}
